import { Box, Grid } from '@radix-ui/themes';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTextInput } from 'components/common/form/text';
import { getSpinTooltipMD } from 'components/sections/pitch-design/input-mode/spin-tooltip';
import { MachineContext } from 'contexts/machine.context';
import { DECIMALS_SPEED, DECIMALS_SPIN } from 'enums/pitches';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { FC, useContext, useEffect, useState } from 'react';
import { usePitchDesignStore } from 'stores/use-pitch-design-store';

export const PDDefaultModeForm: FC = () => {
  const showCurrent = usePitchDesignStore((s) => s.showCurrent);
  const inputs = usePitchDesignStore((s) => s.inputs);
  const refBall = usePitchDesignStore((s) => s.refBall);
  const { speedMph, spins } = inputs;

  const { machine, unitFactors } = useContext(MachineContext);

  const [speedText, setSpeedText] = useState(
    (unitFactors.fromSpeedMph * speedMph).toFixed(DECIMALS_SPEED)
  );

  const latestSpeedMph = useLatest(speedMph);

  // e.g. if we reload machine in the background from admin changes
  useEffect(() => {
    const value = unitFactors.fromSpeedMph * latestSpeedMph.current;
    setSpeedText(value.toFixed(DECIMALS_SPEED));
  }, [unitFactors.fromSpeedMph]);

  const [wxText, setWxText] = useState(spins.wx.toFixed(DECIMALS_SPIN));
  const [wyText, setWyText] = useState(spins.wy.toFixed(DECIMALS_SPIN));
  const [wzText, setWzText] = useState(spins.wz.toFixed(DECIMALS_SPIN));

  return (
    <ErrorBoundary identifier="PDDefaultModeForm">
      <Grid columns="4" gap={RADIX.FLEX.GAP.FORM}>
        <Box>
          <CommonTextInput
            id="pitch-design-speed"
            label={t('pd.speed-units', {
              units: machine.isMetric ? 'kph' : 'mph',
            }).toString()}
            inputColor={
              PitchDesignHelper.validateBallSpeedMph(speedMph)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={speedText}
            onChange={(v) => setSpeedText(v ?? '')}
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ speedMph: v / unitFactors.fromSpeedMph })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: (unitFactors.fromSpeedMph * refBall.speed).toFixed(
                      DECIMALS_SPEED
                    ),
                  }).toString()
                : undefined
            }
          />
        </Box>

        <Box>
          <CommonTextInput
            id="pitch-design-spin-x"
            label="pd.spin-x"
            iconTooltip={getSpinTooltipMD('x')}
            inputColor={
              PitchDesignHelper.validateSpin(spins.wx)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={wxText}
            onChange={(v) => setWxText(v ?? '')}
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ spins: { ...spins, wx: v } })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: refBall.wx.toFixed(DECIMALS_SPIN),
                  }).toString()
                : undefined
            }
          />
        </Box>
        <Box>
          <CommonTextInput
            id="pitch-design-spin-y"
            label="pd.spin-y"
            type="number"
            iconTooltip={getSpinTooltipMD('y')}
            inputColor={
              PitchDesignHelper.validateSpin(spins.wy)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            value={wyText}
            onChange={(v) => setWyText(v ?? '')}
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ spins: { ...spins, wy: v } })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: refBall.wy.toFixed(DECIMALS_SPIN),
                  }).toString()
                : undefined
            }
          />
        </Box>
        <Box>
          <CommonTextInput
            id="pitch-design-spin-z"
            type="number"
            label="pd.spin-z"
            iconTooltip={getSpinTooltipMD('z')}
            inputColor={
              PitchDesignHelper.validateSpin(spins.wz)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            value={wzText}
            onChange={(v) => setWzText(v ?? '')}
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ spins: { ...spins, wz: v } })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: refBall.wz.toFixed(DECIMALS_SPIN),
                  }).toString()
                : undefined
            }
          />
        </Box>
      </Grid>
    </ErrorBoundary>
  );
};
